import { DataType, SortDirection } from "ka-table/enums";
import { Column } from "ka-table/models";
import { Dispatch, SetStateAction, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CrmTable from "../../../../App/Components/Table/CrmTable";
import { useFilteredDeleteCandidatesPagingQuery } from "../../../../App/Queries/CandidateQueries";
import { useCandidatesPaginationStore } from "../../../../App/State/Stores/CandidatesPaginationStore";
import { ICandidate } from "../../../../App/Typings/Candidate";
import { IClient } from "../../../../App/Typings/Client";
import { clearOldLocalStorage, STORAGE_VERSION_DATE } from "../../../../App/Utilities/browserStorage";

export interface IDeleteCandidateTableProps {
  selectedClient?: IClient;
  candidatesToDelete: string[];
  setCandidatesToDelete: Dispatch<SetStateAction<string[]>>;
}

const columnNames: Column[] = [
  {
    title: "Date Added",
    key: "createdDate",
    dataType: DataType.Date,
    sortDirection: SortDirection.Descend,
    isSortable: true,
  },
  { title: "Name", key: "fullName", dataType: DataType.String },
  { title: "Company", key: "company", dataType: DataType.String },
  { title: "Title", key: "title", dataType: DataType.String },
  { title: "Phone Numbers", key: "phoneNumbers", dataType: DataType.Object },
  { title: "Email Addresses", key: "emailAddresses", dataType: DataType.Object },
  { title: "Last Updated", key: "lastUpdatedDate", dataType: DataType.Date },
  { title: "Latest Interacted", key: "lastInteractedWithDate", dataType: DataType.Date },
  { title: "Tags", key: "tags", dataType: DataType.Object },
];

export function DeleteCandidatesTable({
  selectedClient,
  candidatesToDelete,
  setCandidatesToDelete,
}: IDeleteCandidateTableProps) {
  const navigate = useNavigate();
  const {
    filteredCandidateList: tableData,
    isLoading,
    totalItems,
  } = useFilteredDeleteCandidatesPagingQuery(selectedClient?.clientId);

  const key =
    selectedClient && selectedClient.clientId
      ? `delete-candidates-${selectedClient.clientId}-${STORAGE_VERSION_DATE}`
      : `delete-candidates-${STORAGE_VERSION_DATE}`;
  const { page, pageSize, sortBy, sortDirection, handleChangePage, handleChangePageSize, handleRequestSort } =
    useCandidatesPaginationStore(key);

  const isCandidateSelected = (row: ICandidate) => candidatesToDelete.some((id: string) => id === row.id);

  const handleSelectAllClick = (selected: boolean) => {
    if (selected) {
      setCandidatesToDelete(tableData.map((c) => c.id));
      return;
    }
    setCandidatesToDelete([]);
  };

  const handleSelectRow = (row: ICandidate, select: boolean) => {
    if (select) {
      setCandidatesToDelete([...candidatesToDelete, row.id]);
    } else {
      setCandidatesToDelete([...candidatesToDelete.filter((id) => id !== row.id)]);
    }
  };

  const allColumns = useMemo(
    () =>
      columnNames.map((col) => {
        if (col.key === sortBy) {
          return {
            ...col,
            sortDirection: sortDirection === "asc" ? SortDirection.Ascend : SortDirection.Descend,
            isSortable: true,
          };
        } else {
          return {
            ...col,
            sortDirection: undefined,
          };
        }
      }),
    [sortBy, sortDirection],
  );

  return (
    <CrmTable
      name="delete-candidates"
      loading={isLoading}
      columns={allColumns}
      data={tableData}
      onSort={handleRequestSort}
      pagination={{
        totalCount: totalItems,
        rowsPerPage: [10, 25, 50, 100],
        enabled: true,
        currentPage: page,
        rows: pageSize,
        handleChangePage,
        handleChangeRowsPerPage: handleChangePageSize,
      }}
      order={sortDirection}
      orderBy={sortBy}
      onRowClick={(candidate) => {
        navigate(`/admin/candidates/${candidate.id}`);
      }}
      rowSelection={{
        onRowSelect: handleSelectRow,
        isRowSelected: isCandidateSelected,
        onAllSelected: handleSelectAllClick,
      }}
      rowTestId={(candidate) => `candidate-${candidate.id}`}
    />
  );
}

clearOldLocalStorage("delete-candidates");
